import {
  Outlet,
  useLoaderData,
  useRouteLoaderData,
  type LoaderFunctionArgs,
} from "react-router";
import { CaseHeader } from "@/components/case/case-header";
import { CaseProvider } from "@/components/case/case-context";
import { CaseTabs } from "@/components/case/case-tabs";
import { SampleDataBanner } from "@/components/case/sample-data-banner";
import { TraceProvider } from "@/components/case/trace";
import { useDocumentTitle } from "@/lib/document-title";
import type { CaseRecord } from "@/lib/types";
import type { UserProfile } from "@/services/auth";
import { getCaseRecord } from "@/services/cases";

export async function caseLoader({ params }: LoaderFunctionArgs): Promise<CaseRecord> {
  const record = await getCaseRecord(params["caseId"] ?? "");
  // Handled by the root error boundary, which renders "not found" for a 404.
  if (!record) throw new Response("Not found", { status: 404 });
  return record;
}

/**
 * The case workspace.
 *
 * This layout owns the case: it loads the record once, holds it in context, and
 * renders the identity header and tab bar above whichever tab is active. Moving
 * between tabs re-renders only the outlet below — the case never reloads and
 * the analyst never loses context.
 *
 * The actor comes from the workspace route rather than being fetched again:
 * that loader has already resolved the session, and asking the backend who we
 * are a second time per case would be a request that answers nothing new.
 */
export function CaseLayout() {
  const record = useLoaderData<CaseRecord>();
  const user = useRouteLoaderData<UserProfile>("workspace");
  useDocumentTitle(`${record.case.borrowerName} · ${record.case.purpose}`);

  return (
    <CaseProvider record={record} actor={user?.name ?? "Unknown user"}>
      <TraceProvider>
        <div className="flex min-h-full flex-col">
          <SampleDataBanner />
          <CaseHeader />
          <CaseTabs />
          <div className="flex-1 p-6">
            <Outlet />
          </div>
        </div>
      </TraceProvider>
    </CaseProvider>
  );
}
